// Invoice Edit Lock — frontend integration.
//
// The backend puts a 3-minute auto-expiring lease on a Sale while one user has
// it open for editing, so two people can't edit the same invoice at once.
//
// IMPORTANT — one lock for two screens. /rent (the main sale) and /MarketSale
// (the direct sale) are different UIs over the SAME Sale document and the SAME
// endpoints (openSale / updateSaleAll), so an invoice opened on one screen is
// correctly blocked on the other. Both flows use this module.
//
// Responsibilities mirror util/searchLock.js:
//   1. Turn the backend's `409 { status: "locked" }` into a user-facing Kurdish
//      message instead of a click where nothing visibly happens.
//   2. Keep the lease alive with a heartbeat (~30s) while the invoice is open.
//   3. Release the lease when the user saves, starts a new page, or leaves
//      (best-effort; the 3-minute TTL is the real safety net).
//
// Backend contract lives in Util/invoiceLock.js (INVOICE_LOCK_TTL_MS = 180000).
import Instance from "./Instance";

// Must stay well under the backend's 3-minute lease so an actively-editing user
// is never kicked mid-work. Six of these fit inside one lease. Do not raise
// above ~45s.
export const INVOICE_HEARTBEAT_MS = 30000;

// Fallback for the (already-Kurdish) "opened by another user" message, used only
// if the backend ever omits `msg`.
const LOCKED_FALLBACK_MSG =
  "ئەم پسووڵەیە ئێستا لەلایەن بەکارهێنەرێکی ترەوە کراوەتەوە";

// Single active lease per app instance — the app already allows one tab, and a
// user can only have one invoice open for editing at a time. Opening another
// invoice releases the previous one automatically.
let heartbeatTimer = null;
let currentId = null;
let onLostLock = null;

// True when an axios error is the backend's "opened by another user" 409.
export function isInvoiceLocked(error) {
  const r = error && error.response;
  return !!(r && r.status === 409 && r.data && r.data.status === "locked");
}

// The Kurdish "opened by another user" message from the 409 (with a fallback).
export function invoiceLockedMessage(error) {
  const r = error && error.response;
  return (r && r.data && r.data.msg) || LOCKED_FALLBACK_MSG;
}

// The invoice id this app instance currently holds, if any.
export function heldInvoiceId() {
  return currentId;
}

// Best-effort release via the normal axios instance. Never throws.
export function releaseInvoice(id) {
  if (!id) return;
  try {
    Instance.get(`/api/v1/releaseSale/${encodeURIComponent(id)}`).catch(
      () => {}
    );
  } catch (e) {
    /* ignore — the 3-minute TTL is the real guarantee */
  }
}

// Stop the heartbeat and (by default) release the held lease. Pass
// { release: false } when the lease is already gone — either the server just
// cleared it for us (a successful save does) or we lost it to someone else.
export function stopInvoiceHeartbeat(opts) {
  const release = !opts || opts.release !== false;
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  const id = currentId;
  currentId = null;
  onLostLock = null;
  if (id && release) releaseInvoice(id);
}

// One lease ping. Refreshes the lease when we still hold it, re-takes it when
// it lapsed and nobody else claimed it, and reports the loss when someone did.
async function pingLease(id) {
  try {
    await Instance.get(`/api/v1/saleHeartbeat/${encodeURIComponent(id)}`);
  } catch (error) {
    // A ping that lands after the caller moved on says nothing about the
    // invoice they are on now.
    if (currentId !== id) return;
    if (isInvoiceLocked(error)) {
      const cb = onLostLock;
      const msg = invoiceLockedMessage(error);
      // We no longer hold it — stop without a pointless release call.
      stopInvoiceHeartbeat({ release: false });
      if (cb) cb(msg);
    }
    // Network / 404: ignore. The TTL covers us and the next tick may recover.
  }
}

// Start (or restart) the heartbeat for invoice `id`, releasing any previously
// held invoice first. `onLost(msg)` fires if the heartbeat ever comes back 409
// (we lost the lease to someone else after going quiet too long).
export function startInvoiceHeartbeat(id, onLost) {
  stopInvoiceHeartbeat();
  if (!id) return;
  currentId = id;
  onLostLock = typeof onLost === "function" ? onLost : null;
  // Ping once up front, not just on the first tick. This matters on the
  // refresh-restore path: the invoice comes back on screen as "open" without
  // having gone through openSale, so this is what proves it is still ours —
  // and tells the user straight away if it isn't.
  pingLease(id);
  heartbeatTimer = setInterval(() => pingLease(id), INVOICE_HEARTBEAT_MS);
}

// Unload release. Uses fetch + keepalive (NOT navigator.sendBeacon) so the call
// survives navigation AND can still send the Authorization header — the
// backend's `protect` middleware rejects header-less requests, which a beacon
// would be.
export function releaseInvoiceOnUnload() {
  if (!currentId) return;
  const id = currentId;
  try {
    fetch(
      `${Instance.defaults.baseURL}/api/v1/releaseSale/${encodeURIComponent(
        id
      )}`,
      {
        method: "GET",
        headers: { Authorization: "Bearer " + localStorage.token },
        keepalive: true
      }
    ).catch(() => {});
  } catch (e) {
    /* ignore — the 3-minute TTL is the real guarantee */
  }
}
